import { ChangeDetectionStrategy, Component, inject, output } from '@angular/core';
import { SVGIconModule } from '@progress/kendo-angular-icons';
import { sparklesIcon } from '@progress/kendo-svg-icons';
import { CasiChatStore } from '../../services/casi-chat.store';

/**
 * The CASI launcher: a vertical "Ask CASI" tab centered on the right edge of every page, in the
 * app's AI gradient (--gradient-brand). Emits `open`; the app shell opens the slideout on the org
 * being viewed. Hidden while the slideout is open (it has its own close button). The shell only
 * renders it for internal Cyflare admins on CASI builds.
 */
@Component({
  selector: 'app-casi-side-tab',
  standalone: true,
  imports: [SVGIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (!store.panelOpen()) {
      <button
        type="button"
        class="casi-tab"
        aria-label="Open CASI chat"
        title="Ask CASI about what you're looking at"
        (click)="open.emit()"
      >
        <kendo-svgicon
          class="casi-tab__icon"
          [icon]="sparklesIcon"
        ></kendo-svgicon>
        <span class="casi-tab__label">Ask CASI</span>
      </button>
    }
  `,
  styles: [
    `
      .casi-tab {
        position: fixed;
        top: 50%;
        right: 0;
        z-index: 999; /* under the slideout (1000) */
        /* Vertical, reading bottom-to-top like a typical edge tab. */
        writing-mode: vertical-rl;
        transform: translateY(-50%) rotate(180deg);
        display: flex;
        align-items: center;
        gap: 0.5rem;
        overflow: hidden;
        border: 0;
        border-radius: 0 10px 10px 0; /* rounded on the screen-facing side once rotated */
        padding: 0.875rem 0.5rem;
        background: var(--gradient-brand);
        color: #fff;
        font-size: 0.8125rem;
        font-weight: 600;
        letter-spacing: 0.04em;
        cursor: pointer;
        box-shadow: 2px 0 14px -2px rgba(99, 102, 241, 0.6);
        transition:
          padding 0.15s ease,
          box-shadow 0.15s ease;
        animation: casi-tab-in 0.5s ease-out both;
      }
      .casi-tab:hover {
        padding-bottom: 0.875rem;
        padding-top: 0.875rem;
        padding-right: 0.75rem; /* grows out from the edge (rotated) */
        box-shadow: 2px 0 22px 0 rgba(99, 102, 241, 0.75);
      }
      .casi-tab:focus-visible {
        outline: 2px solid #fff;
        outline-offset: -4px;
      }
      /* One sweep of light shortly after it appears, to draw the eye. */
      .casi-tab::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(200deg, transparent 30%, rgba(255, 255, 255, 0.5) 50%, transparent 70%);
        transform: translateY(-120%);
        animation: casi-tab-shimmer 1.6s ease-out 1s 1 both;
        pointer-events: none;
      }
      .casi-tab:hover::after {
        animation: casi-tab-shimmer 1s ease-out 1 both;
      }
      .casi-tab__icon {
        /* Counter the tab's 180deg turn so the sparkle stays upright. */
        transform: rotate(180deg);
        width: 1.125rem;
        height: 1.125rem;
      }
      @keyframes casi-tab-in {
        from {
          opacity: 0;
          transform: translate(-100%, -50%) rotate(180deg);
        }
        to {
          opacity: 1;
          transform: translateY(-50%) rotate(180deg);
        }
      }
      @keyframes casi-tab-shimmer {
        from {
          transform: translateY(-120%);
        }
        to {
          transform: translateY(120%);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .casi-tab,
        .casi-tab::after {
          animation: none !important;
          transition: none;
        }
      }
    `,
  ],
})
export class CasiSideTabComponent {
  /** Open the CASI slideout. */
  open = output<void>();

  protected store = inject(CasiChatStore);
  protected readonly sparklesIcon = sparklesIcon;
}
